import { getRelationshipDirection } from '@reach/shared-types/conditions';
import type { SchemaDisplayMetadata } from '@reach/shared-types/schema-mappings';
import {
  getRelationshipOptionsForSubject,
  type SchemaRelationshipGraph,
} from '@reach/shared-types/segment-schema-graph';
import type { CSSProperties } from 'react';

/** Low alpha so the schema color reads as a wash behind text without hurting contrast. */
const TINT_ALPHA = 0.06;

function hexToRgba(hex: string, alpha: number): string | undefined {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return undefined;
  const value = parseInt(match[1], 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Uses `rgba()` rather than `color-mix()` — the latter has no precedent here and partner-dashboard sets no build target. */
export function schemaTintStyle(displayMetadata?: SchemaDisplayMetadata | null): CSSProperties {
  let color = displayMetadata?.color;
  let tint = color ? hexToRgba(color, TINT_ALPHA) : undefined;
  // Custom properties inherit, so an uncolored schema resets them rather than showing its parent's color.
  if (!color || !tint) {
    color = 'initial';
    tint = 'initial';
  }
  return { '--schema-color': color, '--schema-tint': tint } as CSSProperties;
}

/** Keyed on the resolved related schema — the block's "other end" per `getRelationshipDirection`, NOT raw `targetSchemaId`. */
export function ambiguousRelatedSchemaIds(
  graph: SchemaRelationshipGraph,
  subjectSchemaId: string | null | undefined,
): Set<string> {
  if (!subjectSchemaId) return new Set();
  const countByRelatedSchemaId = new Map<string, number>();
  for (const relationship of getRelationshipOptionsForSubject({ graph, subjectSchemaId })) {
    const { relatedSchemaId } = getRelationshipDirection({ relationship, subjectSchemaId });
    countByRelatedSchemaId.set(relatedSchemaId, (countByRelatedSchemaId.get(relatedSchemaId) ?? 0) + 1);
  }
  return new Set(
    [...countByRelatedSchemaId].filter(([, count]) => count > 1).map(([schemaId]) => schemaId),
  );
}
